import { createFileRoute, Link } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { ArrowRight, Mail } from "lucide-react";
import { currentBrand } from "@/lib/brand";
import { helpCategories, customerTopics, staffTopics, docsTopics } from "@/lib/help-content";
import { HelpSearch } from "@/components/help/HelpSearch";

export const Route = createFileRoute("/help")({
  component: HelpIndex,
});

function HelpIndex() {
  const { profile } = useAuth();
  const isStaff = profile?.role === "staff" || profile?.role === "admin";
  const categories = helpCategories.filter(
    (c) => c.audience === "all" || (c.audience === "staff" && isStaff),
  );

  const searchTopics = [
    ...customerTopics.map((t) => ({
      ...t,
      sectionLabel: "Customers",
      sectionLink: "/help/customers",
    })),
    ...(isStaff
      ? [
          ...staffTopics.map((t) => ({
            ...t,
            sectionLabel: "Staff",
            sectionLink: "/help/staff",
          })),
          ...docsTopics.map((t) => ({
            ...t,
            sectionLabel: "Docs",
            sectionLink: "/help/docs",
          })),
        ]
      : []),
  ];

  const backLink = profile ? "/app/customer/dashboard" : "/";

  return (
    <div className="min-h-screen bg-background animate-in fade-in slide-in-from-bottom-4 duration-200 flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-background">
        <div className="mx-auto max-w-6xl flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <img
              src={currentBrand.logoSrc}
              alt={`${currentBrand.name} logo`}
              className="h-8 w-auto"
            />
            <h1 className="text-lg font-semibold">FlightDeck Help</h1>
          </div>
          <Link
            to={backLink}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Back to {profile ? "Dashboard" : "FlightDeck"}
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <div className="mx-auto max-w-4xl px-6 py-12">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Help Center</h1>
            <p className="text-muted-foreground">
              {isStaff
                ? "Welcome to the FlightDeck help center for staff."
                : "Welcome to the FlightDeck help center for customers."}
            </p>
          </div>

          <div className="mb-8">
            <HelpSearch topics={searchTopics} />
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <Link
                  key={category.link}
                  to={category.link}
                  className="group rounded-3xl border border-border bg-card p-6 shadow-soft hover:border-primary/50 transition-all hover:shadow-lg"
                >
                  <div className="flex items-start gap-4">
                    <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-hero text-primary-foreground">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                        {category.title}
                      </h2>
                      <p className="text-sm text-muted-foreground">{category.description}</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-sky-50 dark:bg-sky-950/20 py-8 border-t border-border">
        <div className="mx-auto max-w-6xl px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img
                src={currentBrand.logoSrc}
                alt={`${currentBrand.name} logo`}
                className="h-6 w-auto"
              />
              <span className="text-sm font-medium">{currentBrand.name}</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-foreground transition-colors">
                Home
              </Link>
              <Link to="/pricing" className="hover:text-foreground transition-colors">
                Pricing
              </Link>
              <a
                href="mailto:support@resoluteink.co.uk"
                className="flex items-center gap-1 hover:text-foreground transition-colors"
              >
                <Mail className="h-4 w-4" /> Support
              </a>
            </div>
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} {currentBrand.name}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
